"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhatIsDirectPartMarking() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  const {
    ref: paraRef,
    isVisible: paraVisible
  } = useRevealOnScroll<HTMLParagraphElement>();
  const {
    ref: pillarsRef,
    isVisible: pillarsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const pillars = [{
    number: "01",
    title: "Precision",
    description: "Achieving microscopic accuracy through advanced laser calibration."
  }, {
    number: "02",
    title: "Permanence",
    description: "Creating marks that withstand the harshest industrial environments."
  }, {
    number: "03",
    title: "Traceability",
    description: "Ensuring full lifecycle visibility for every critical component."
  }];
  return <section data-section-id="243" id="what-is-direct-part-marking" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-44">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div className="lg:col-span-7 relative">
            {}
            <div ref={eyebrowRef} className={`font-mono text-xs tracking-[0.25em] uppercase mb-10 transition-all duration-700 ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            color: "var(--primary)"
          }}>
              <span>Technical Process</span>
              <span className="mx-3 opacity-50">/</span>
              <span>01</span>
            </div>

            {}
            <h2 ref={headingRef} className={`font-default font-light tracking-tight leading-[0.95] mb-12 transition-all duration-1000 ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            fontSize: "clamp(3rem, 6vw, 6rem)",
            color: "var(--foreground)"
          }}>
              Direct Part{" "}
              <em className="italic font-default" style={{
              color: "var(--primary)"
            }}>
                Marking
              </em>{" "}
              Explained
            </h2>

            {}
            <p ref={paraRef} className={`font-default text-[20px] leading-[1.7] max-w-[520px] mb-16 transition-all duration-1000 delay-100 ${paraVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            color: "var(--muted-foreground)"
          }}>
              Direct Part Marking (DPM) is the process of permanently imprinting data directly onto a component's surface. By utilizing high-energy laser systems, we ensure that serial numbers, barcodes, and logos remain legible throughout the entire lifespan of the part, regardless of environmental exposure or mechanical wear.
            </p>

            {}
            <div ref={pillarsRef} className={`grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8 mb-16 transition-all duration-1000 delay-200 ${pillarsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {pillars.map((pillar, i) => <div data-index={i} key={pillar.number} className="space-y-3">
                  <div className="font-mono text-xs tracking-[0.2em]" style={{
                color: "var(--primary)"
              }}>
                    {pillar.number}
                  </div>
                  <h3 className="font-default text-2xl font-medium" style={{
                color: "var(--foreground)"
              }}>
                    {pillar.title}
                  </h3>
                  <p className="font-default text-sm leading-relaxed" style={{
                color: "var(--muted-foreground)"
              }}>
                    {pillar.description}
                  </p>
                </div>)}
            </div>

            {}
            <div className="mt-12 max-w-md space-y-[6px] opacity-40">
              <div className="h-px w-full" style={{
              backgroundColor: "var(--foreground)"
            }} />
              <div className="h-px w-full" style={{
              backgroundColor: "var(--foreground)"
            }} />
              <div className="h-px w-full" style={{
              backgroundColor: "var(--foreground)"
            }} />
              <div className="h-px w-full" style={{
              backgroundColor: "var(--foreground)"
            }} />
              <div className="h-px w-full" style={{
              backgroundColor: "var(--foreground)"
            }} />
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-5 relative lg:-mt-32 lg:-mb-32 transition-all duration-1200 ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative aspect-[3/4] lg:aspect-[3/4.5] w-full overflow-hidden rounded-lg shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_dpm_industrial_process.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_dpm_industrial_process.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_dpm_industrial_process.webp-wvc-sizes" alt="Industrial laser marking process on metal component" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              {}
              <div className="absolute inset-0 mix-blend-multiply pointer-events-none" style={{
              background: "linear-gradient(135deg, hsla(45, 68%, 47%, 0.2) 0%, hsla(216, 40%, 10%, 0.6) 100%)"
            }} />
              <div className="absolute inset-0 mix-blend-overlay pointer-events-none" style={{
              background: "linear-gradient(180deg, hsla(45, 68%, 60%, 0.1) 0%, hsla(216, 40%, 5%, 0.5) 100%)"
            }} />
            </div>

            {}
            <div className="mt-6 flex items-center gap-4">
              <div className="h-px flex-1" style={{
              backgroundColor: "var(--border)"
            }} />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{
              color: "var(--muted-foreground)"
            }}>
                Plate 01 — DPM
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}